// TODO: Task 904718: refactor to use explicit dependency for es6 syntax rather than global variable
/* global Map */
export type ShadowTreeRef = string;
export type ShadowTreeNodeItem = unknown;

export interface ShadowTreeNode<T = ShadowTreeNodeItem> {
  parent: T | null;
  ref: ShadowTreeRef | null;
  children: Map<ShadowTreeRef, T>;
}

export type ShadowTreeParentAndRef<T = ShadowTreeNodeItem> = Pick<ShadowTreeNode<T>, 'parent' | 'ref'>;

export interface ShadowTreeRuntime<T = ShadowTreeNodeItem> {
  nodes: Map<T | null, ShadowTreeNode<T>>;
}

function getMapKeys<K, V>(map: Map<K, V>): K[] {
  const keys: K[] = [];

  map.forEach((value: V, key: K) => keys.push(key));
  return keys;
}

function getMapValues<K, V>(map: Map<K, V>): V[] {
  const values: V[] = [];

  map.forEach((value: V) => values.push(value));
  return values;
}

function getParentAndRef<T>(tree: ShadowTreeRuntime<T>, item: T | null): ShadowTreeParentAndRef<T> {
  const { parent, ref } = tree.nodes.get(item) as ShadowTreeNode<T>;
  return { parent, ref };
}

function getChildren<T>(tree: ShadowTreeRuntime<T>, item: T | null): Map<ShadowTreeRef, T> {
  return (tree.nodes.get(item) as ShadowTreeNode<T>).children;
}

function getChild<T>(tree: ShadowTreeRuntime<T>, item: T | null, ref: ShadowTreeRef): T | undefined {
  return getChildren(tree, item).get(ref);
}

function createNode<T>(tree: ShadowTreeRuntime<T>, item: T, parent: T | null, ref: ShadowTreeRef) {
  tree.nodes.set(item, {
    parent,
    ref,
    children: new Map(),
  });

  getChildren(tree, parent).set(ref, item);
}

function removeNode<T>(tree: ShadowTreeRuntime<T>, item: T) {
  const { parent, ref } = tree.nodes.get(item) as ShadowTreeNode<T>;

  removeNodeChildren(tree, item); // eslint-disable-line no-use-before-define
  getChildren(tree, parent).delete(ref as ShadowTreeRef);
  tree.nodes.delete(item);
}

function removeNodeChildren<T>(tree: ShadowTreeRuntime<T>, item: T | null) {
  getChildren(tree, item).forEach((child: T) => removeNode(tree, child));
}

function moveChild<T>(tree: ShadowTreeRuntime<T>, parentOld: T | null, refOld: ShadowTreeRef, parentNew: T | null, refNew: ShadowTreeRef) {
  const childrenOld = getChildren(tree, parentOld);
  const childrenNew = getChildren(tree, parentNew);
  const item = childrenOld.get(refOld) as T;
  const node = tree.nodes.get(item) as ShadowTreeNode<T>;

  childrenOld.delete(refOld);
  childrenNew.set(refNew, item);
  node.parent = parentNew;
  node.ref = refNew;
}

function getPathOfItem<T>(tree: ShadowTreeRuntime<T>, item: T | null) {
  const refs: Array<ShadowTreeRef | null> = [];
  let itemIter = item;

  while (itemIter) {
    const { parent, ref } = getParentAndRef(tree, itemIter);

    refs.push(ref);
    itemIter = parent as T;
  }

  refs.reverse();

  return `/${refs.join('/')}`;
}

function getItemByPath<T>(tree: ShadowTreeRuntime<T>, path: string): T | null | undefined {
  const refs = path.split('/');
  let item: T | null | undefined = null;

  for (let index = 0; index < refs.length; index += 1) {
    const ref = refs[index];

    if (ref) {
      item = getChild(tree, item as T | null, ref);

      if (!item) {
        break;
      }
    }
  }

  return item;
}

/**
 * A nonintrusive tree model.
 * You may use this class to setup a hierarchical structure among a set of items
 * without injecting properties into them.
 * With this tree model, each edge has a name called 'ref'. For a given node,
 * the 'ref's to each child MUST be unique.
 * NOTE: The root element of the tree is always `null`.
 */
export class ShadowTree<T = ShadowTreeNodeItem> {
  constructor() {
    const tree = this as this & ShadowTreeRuntime<T>;

    tree.nodes = new Map();
    tree.nodes.set(null, {
      parent: null,
      children: new Map(),
      ref: null,
    });
  }

  /**
   * Test if an item exists in the tree
   * @param {Object} item - The item to test
   * @returns {boolean} - True if the item exists in the tree
   */
  hasNode(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    return tree.nodes.has(item);
  }

  /**
   * Get the parent item and ref for the given item
   * @param {Object} item - The issued item
   * @returns {{parent: Object, ref: string}} - The parent item and the ref
   */
  getParentAndRef(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getParentAndRef(tree, item);
  }

  /**
   * Get refs for all children of the given item
   * @param {Object} item - The issued item
   * @returns {string[]} - An array of all refs
   */
  getChildrenRefs(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getMapKeys(getChildren(tree, item));
  }

  /**
   * Get all children of the given item
   * @param {Object} item - The issued item
   * @returns {Object[]} - An array of all children
   */
  getChildren(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getMapValues(getChildren(tree, item));
  }

  /**
   * Get the child of the given item with the specified ref
   * @param {Object} item - The issued item
   * @param {string} ref - The ref for the child
   * @returns {Object} - The child with the given ref. Returns `undefined` if
   * there's no child with the given ref
   */
  getChild(item: T | null, ref: ShadowTreeRef) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getChild(tree, item, ref);
  }

  /**
   * Add a new node in the tree with the given item
   * @param {Object} item - The item of the new node
   * @param {Object} parent - The item of the parent node
   * @param {string} ref - The ref to specified the new node from the parent
   * @returns {void}
   */
  createNode(item: T, parent: T | null, ref: ShadowTreeRef) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (typeof item === 'undefined') {
      throw new Error('The item cannot be undefined');
    }

    if (this.hasNode(item)) {
      throw new Error('Duplicated node creation');
    }

    if (!this.hasNode(parent)) {
      throw new Error('The parent item is not in the tree');
    }

    if (getChild(tree, parent, ref)) {
      throw new Error('Cannot override existing child');
    }

    return createNode(tree, item, parent, ref);
  }

  /**
   * Remove all descendants of a given item
   * @param {Object} item - The issued item
   * @returns {void}
   */
  removeNodeChildren(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return removeNodeChildren(tree, item);
  }

  /**
   * Remove an item from the tree
   * @param {Object} item - The item to remove
   * @returns {void}
   */
  removeNode(item: T) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    if (item === null) {
      throw new Error('Cannot remove the virtual root node');
    }

    return removeNode(tree, item);
  }

  /**
   * Move an item to a new place in the tree
   * @param {Object} parentOld - The old parent of the item
   * @param {string} refOld - The old ref of the item
   * @param {Object} parentNew - The new parent of the item
   * @param {string} refNew - The new ref of the item
   * @returns {void}
   */
  moveChild(parentOld: T | null, refOld: ShadowTreeRef, parentNew: T | null, refNew: ShadowTreeRef) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (parentNew === parentOld && refOld === refNew) {
      return;
    }

    if (!this.hasNode(parentOld)) {
      throw new Error('The old parent is not in the tree');
    }

    if (!this.hasNode(parentNew)) {
      throw new Error('The new parent is not in the tree');
    }

    if (!getChild(tree, parentOld, refOld)) {
      throw new Error('The child to move does not exist');
    }

    if (getChild(tree, parentNew, refNew)) {
      throw new Error('Cannot override existing child');
    }

    moveChild(tree, parentOld, refOld, parentNew, refNew);
  }

  /**
   * Get a '/' separated full path of a given item, which identifies an unique
   * node in the tree
   * @param {Object} item - The issued item
   * @returns {string} - The full path to direct to the item from root
   */
  getPathOfItem(item: T | null) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getPathOfItem(tree, item);
  }

  /**
   * Query an item from the tree with its path
   * @param {string} path - The '/' separated path
   * @returns {Object} - The item at the path or `undefined` if not found
   */
  getItemByPath(path: string) {
    const tree = this as this & ShadowTreeRuntime<T>;

    if ((!path[0] as unknown) === '/') {
      throw new Error('Expect an absolute path');
    }

    return getItemByPath(tree, path);
  }
}
